import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as Sentry from "@sentry/react";
import { useUserRoles } from "../hooks/useUserRoles";
import { onWriteFailure } from "../utils/offlineWrites";

export interface AppError {
  id: number;
  message: string;
  code: string;
  stack?: string;
  timestamp: Date;
  details: unknown;
}

interface ErrorContextValue {
  errors: AppError[];
  addError: (error: unknown) => void;
  dismissError: (errorId: number) => void;
  clearAll: () => void;
  canViewErrors: boolean;
}

const ErrorContext = createContext<ErrorContextValue | null>(null);

export function useErrors(): ErrorContextValue {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error("useErrors must be used within an ErrorProvider");
  }
  return context;
}

interface ErrorLike {
  message?: string;
  code?: string;
  stack?: string;
}

function toErrorLike(input: unknown): ErrorLike {
  if (input instanceof Error) {
    return {
      message: input.message,
      code: (input as unknown as { code?: string }).code,
      stack: input.stack,
    };
  }
  if (typeof input === "object" && input !== null) {
    const obj = input as Record<string, unknown>;
    return {
      message: typeof obj.message === "string" ? obj.message : String(input),
      code: typeof obj.code === "string" ? obj.code : undefined,
      stack: typeof obj.stack === "string" ? obj.stack : undefined,
    };
  }
  return { message: String(input) };
}

const DEDUPE_WINDOW_MS = 1000;
const CLEANUP_WINDOW_MS = 2000;
const AUTO_DISMISS_MS = 10000;

export function ErrorProvider({ children }: { children: ReactNode }) {
  const [errors, setErrors] = useState<AppError[]>([]);
  const recentErrorsRef = useRef<Map<string, number>>(new Map());
  const { hasRole } = useUserRoles();
  const canViewErrors = hasRole("view_error_details");

  const addError = useCallback(
    (rawError: unknown) => {
      // Always report to Sentry regardless of UI visibility
      Sentry.captureException(rawError);

      if (!canViewErrors) return;

      const errorLike = toErrorLike(rawError);
      const errorKey = `${errorLike.message ?? ""}_${errorLike.code ?? "unknown"}`;
      const now = Date.now();

      // Deduplicate within the window
      const lastSeen = recentErrorsRef.current.get(errorKey);
      if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) {
        return;
      }

      // Record + cleanup old entries
      recentErrorsRef.current.set(errorKey, now);
      for (const [key, ts] of recentErrorsRef.current.entries()) {
        if (now - ts > CLEANUP_WINDOW_MS) {
          recentErrorsRef.current.delete(key);
        }
      }

      const errorObj: AppError = {
        id: now + Math.random(),
        message: errorLike.message || "Unknown error",
        code: errorLike.code || "unknown",
        stack: errorLike.stack,
        timestamp: new Date(),
        details: rawError,
      };

      setErrors((prev) => [...prev, errorObj]);

      // Auto-dismiss after window
      setTimeout(() => {
        setErrors((prev) => prev.filter((e) => e.id !== errorObj.id));
      }, AUTO_DISMISS_MS);
    },
    [canViewErrors]
  );

  const dismissError = useCallback((errorId: number) => {
    setErrors((prev) => prev.filter((e) => e.id !== errorId));
  }, []);

  const clearAll = useCallback(() => {
    setErrors([]);
  }, []);

  // Rejected background writes. Unlike `errors` these are for everyone, not
  // just `view_error_details` holders: the write already vanished from the
  // screen (Firestore rolled it back), and the person who made it is the one
  // who needs to know to redo it. Kept until dismissed for the same reason.
  const [writeNotices, setWriteNotices] = useState<{ id: number; message: string }[]>([]);

  useEffect(
    () =>
      onWriteFailure((failure) => {
        Sentry.captureException(failure.error, { extra: { context: failure.context } });
        setWriteNotices((prev) =>
          // One rejected rule often fails a run of writes; say it once.
          prev.some((n) => n.message === failure.message)
            ? prev
            : [...prev, { id: Date.now() + Math.random(), message: failure.message }],
        );
      }),
    [],
  );

  return (
    <ErrorContext.Provider
      value={{ errors, addError, dismissError, clearAll, canViewErrors }}
    >
      {children}
      {writeNotices.length > 0 && (
        // Reuses the error toast's look, pinned to the top so it doesn't sit
        // on the details toast (bottom) for people who see both.
        <div className="error-toast-container" style={{ top: "1rem", bottom: "auto" }} role="alert">
          <div className="error-toast-list">
            {writeNotices.map((notice) => (
              <div key={notice.id} className="error-toast-item">
                <div className="error-toast-item-header">
                  <div className="error-toast-message">{notice.message}</div>
                  <button
                    type="button"
                    className="error-toast-dismiss"
                    title="Dismiss"
                    onClick={() =>
                      setWriteNotices((prev) => prev.filter((n) => n.id !== notice.id))
                    }
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </ErrorContext.Provider>
  );
}
